import { useEffect, useRef } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import { Button, Divider, Grid, Stack } from '@mui/material';
import { useMap } from '@vis.gl/react-google-maps';
import moment from 'moment';

import { MapLayerApi } from '@/api';
import { AppModal, FormRoot, RenderFormField } from '@/components';
import { MapBounds } from '@/components/GoogleMap';
import { useMarkerTypes } from '@/hooks/useMarkerTypes';
import { IPointLayer, PointLayerType, PointLayerTypeLabel } from '@/models';
import { useToastStore } from '@/store';

import { syncPlaceAddressToMasterIndex } from './addressSync';

const FORM_DATE_FORMAT = 'YYYY-MM-DD';
const FORM_TIME_FORMAT = 'HH:mm';

interface CreateNewMarkerModalProps {
  open: boolean;
  onClose: () => void;
  marker?: IPointLayer;
  place?: google.maps.places.PlaceResult;
  onNewMarker?: (marker: IPointLayer) => void;
  onUpdateMarker?: (marker: IPointLayer) => void;
  fetchMapPoints?: (bounds: MapBounds) => void;
}

const getMarkerFormFields = (
  markerTypes: any[],
  isLoading: boolean,
  error: Error | null,
) => [
  {
    name: 'properties.address',
    label: 'Address',
    type: 'text',
    grid: { xs: 12 },
  },
  {
    name: 'properties.expirationDate',
    label: 'Expiration Date',
    type: 'date',
    grid: { xs: 6 },
  },
  {
    name: 'properties.expirationTime',
    label: 'Expiration Time',
    type: 'time',
    ampm: false,
    grid: { xs: 6 },
  },
  {
    name: 'type',
    label: 'Marker Name',
    type: 'select',
    placeholder: 'Select marker type...',
    required: true,
    rules: { required: 'This field is required.' },
    items:
      markerTypes.length > 0
        ? markerTypes.map((type) => ({
            label: type.name,
            value: type.name,
          }))
        : Object.entries(PointLayerTypeLabel).map(([value, label]) => ({
            label,
            value,
          })),
    disabled: isLoading,
    helperText: error
      ? `Error loading marker types: ${error.message}. Using default options.`
      : isLoading
      ? 'Loading marker types...'
      : markerTypes.length === 0
      ? 'No marker types configured. Using default options.'
      : undefined,
    grid: { xs: 12 },
  },
  {
    name: 'properties.description',
    label: 'Description',
    type: 'text',
    grid: { xs: 12 },
  },
  {
    name: 'active',
    label: 'Active',
    type: 'checkbox',
    grid: { xs: 12 },
  },
];

const getDefaultFormValues = (place?: google.maps.places.PlaceResult) => ({
  properties: {
    address:
      place?.formatted_address ||
      (place?.geometry
        ? `${place.geometry.location?.lat()}, ${place.geometry.location?.lng()}`
        : ''),
    description: '',
    expirationDate: null,
    expirationTime: null,
  },
  type: '', // Leave empty so user must select
  active: true,
});

const CreateNewMarkerModal = ({
  open,
  onClose,
  marker,
  place,
  onNewMarker,
  onUpdateMarker,
  fetchMapPoints,
}: CreateNewMarkerModalProps) => {
  const { enabledMarkerTypes, isLoading, error } = useMarkerTypes();
  const map = useMap();
  const { updateToast } = useToastStore();
  const methods = useForm({
    mode: 'all',
  });
  const { reset, setValue, getValues, control, trigger } = methods;
  const isInitialized = useRef(false);
  const previousOpen = useRef(false);
  const previousMarkerType = useRef<string | undefined>(undefined);

  // Watch for marker type changes
  const selectedMarkerType = useWatch({
    control,
    name: 'type',
  });

  const onSubmit = async () => {
    const values = getValues();

    const isValid = await trigger();

    if (!isValid || !values.type || values.type === '') {
      return;
    }

    try {
      // convert expiration date/time from local to UTC before sending
      let formattedDateTime: number | undefined;

      if (values.properties?.expirationDate) {
        const datePart = moment(values.properties.expirationDate).format(
          FORM_DATE_FORMAT,
        );
        const timePart = values.properties?.expirationTime
          ? moment(values.properties.expirationTime).format(FORM_TIME_FORMAT)
          : '23:59';

        // Combine date and time with moment and convert to UTC
        const combinedUTCTimestamp = moment(
          `${datePart} ${timePart}`,
          `${FORM_DATE_FORMAT} ${FORM_TIME_FORMAT}`,
        )
          .utc()
          .unix();

        formattedDateTime = combinedUTCTimestamp;
      }

      // Handle empty description - backend might not accept empty strings
      const description = values.properties?.description?.trim();
      const address = values.properties?.address?.trim();

      const selectedType = enabledMarkerTypes.find(
        (t) => t.name === values.type,
      ) || { name: values.type, defaultExpirationTime: 0 };
      const markerTypeName = selectedType.name;
      const markerTypeDefaultExpirationTime =
        selectedType.defaultExpirationTime;
      const expirationDateTime =
        formattedDateTime ||
        (markerTypeDefaultExpirationTime &&
          moment().unix() + markerTypeDefaultExpirationTime);

      const apiProperties = {
        address: address || '',
        ...(expirationDateTime && { expirationDateTime }),
        // Only include description if it has a value
        ...(description && { description }),
      };

      if (marker) {
        const updateMarker: IPointLayer = {
          _id: marker._id,
          name: marker.name,
          point: marker.point,
          type: markerTypeName,
          properties: apiProperties,
          createdBy: marker.createdBy,
          isImported: marker.isImported,
        };
        const response = await MapLayerApi.updatePointLayer(
          marker._id as string,
          updateMarker,
        );
        onUpdateMarker?.(response.data);
      } else {
        if (!place) return;

        const savedAddress = await syncPlaceAddressToMasterIndex(place);
        const createMarker: IPointLayer = {
          name: `${markerTypeName} ${place.geometry?.location?.lng()} ${place.geometry?.location?.lat()}`,
          type: markerTypeName,
          point: {
            type: 'Point',
            coordinates: [
              place.geometry?.location?.lng() ?? 0,
              place.geometry?.location?.lat() ?? 0,
            ],
          },
          properties: {
            ...apiProperties,
            address: savedAddress.addressLabel || apiProperties.address,
          },
        };

        const response = await MapLayerApi.createPointLayer(createMarker);
        onNewMarker?.(response.data);
      }

      const bounds = map?.getBounds();
      const zoom = map?.getZoom() || 0;
      if (zoom > 15 && bounds) {
        fetchMapPoints?.({
          north: bounds.getNorthEast().lat(),
          south: bounds.getSouthWest().lat(),
          east: bounds.getNorthEast().lng(),
          west: bounds.getSouthWest().lng(),
        });
      }

      reset();
      onClose();
    } catch (err: any) {
      updateToast({
        open: true,
        message: err?.message || 'Failed to save marker.',
      });
    }
  };

  useEffect(() => {
    if (open && !previousOpen.current) {
      isInitialized.current = false;
      previousMarkerType.current = undefined;
    }

    previousOpen.current = open;

    if (!open) {
      isInitialized.current = false;
      previousMarkerType.current = undefined;
      return;
    }

    if (isInitialized.current) {
      return;
    }

    if (marker) {
      reset();

      setValue('properties.address', marker.properties?.address || '');
      setValue('properties.description', marker.properties?.description || '');
      const expirationMoment = marker.properties?.expirationDateTime
        ? // eslint-disable-next-line import/no-named-as-default-member
          moment.unix(marker.properties.expirationDateTime)
        : null;
      setValue(
        'properties.expirationDate',
        expirationMoment ? expirationMoment.toDate() : null,
      );
      setValue(
        'properties.expirationTime',
        expirationMoment ? expirationMoment.toDate() : null,
      );

      if (enabledMarkerTypes.length > 0) {
        const matchingAdminType = enabledMarkerTypes.find(
          (type) => type.name === marker.type,
        );

        if (matchingAdminType) {
          setValue('type', matchingAdminType.name);
        } else {
          const markerTypeLabel =
            PointLayerTypeLabel[marker.type as PointLayerType];
          const matchingAdminTypeByLabel = enabledMarkerTypes.find(
            (type) => type.name === markerTypeLabel,
          );
          setValue(
            'type',
            matchingAdminTypeByLabel?.name ||
              enabledMarkerTypes[0]?.name ||
              marker.type,
          );
        }
      } else {
        setValue('type', marker.type);
      }
    } else {
      const defaultValues = getDefaultFormValues(place);
      reset(defaultValues);
    }

    isInitialized.current = true;
  }, [marker, open, reset, setValue, enabledMarkerTypes, place]);

  useEffect(() => {
    if (!selectedMarkerType) {
      previousMarkerType.current = undefined;
      return;
    }

    // Check if the type actually changed (including first selection)
    const typeChanged = previousMarkerType.current !== selectedMarkerType;
    previousMarkerType.current = selectedMarkerType;

    // Skip if type didn't change (e.g., just enabledMarkerTypes finished loading)
    if (!typeChanged) {
      return;
    }

    // Only auto-fill expiration for NEW markers when selecting a type with default expiration
    // For EDIT markers, don't touch the fields - preserve user's changes
    if (!marker) {
      const selectedType = enabledMarkerTypes.find(
        (t) => t.name === selectedMarkerType,
      );

      if (selectedType?.defaultExpirationTime) {
        const expirationMoment = moment().add(
          selectedType.defaultExpirationTime,
          'seconds',
        );

        setValue('properties.expirationDate', expirationMoment);
        setValue('properties.expirationTime', expirationMoment);
      }
    }
  }, [selectedMarkerType, enabledMarkerTypes, marker, setValue]);

  return (
    <AppModal
      open={open}
      title={marker ? 'Edit Marker' : 'Create New Marker'}
      onClose={() => {
        reset();
        onClose();
      }}
    >
      <>
        <FormProvider {...methods}>
          <FormRoot>
            <Grid columnSpacing={2} flex={1} container>
              {getMarkerFormFields(enabledMarkerTypes, isLoading, error).map(
                ({ grid, ...rest }) => {
                  return (
                    <Grid key={rest.name} {...grid} item>
                      <RenderFormField {...rest} />
                    </Grid>
                  );
                },
              )}
            </Grid>
          </FormRoot>
          <Divider sx={{ mb: 2 }} />
          <Stack direction="row" gap={2} justifyContent="flex-end">
            <Button
              onClick={() => {
                reset();
                onClose();
              }}
              variant="outlined"
              color="error"
            >
              Cancel
            </Button>
            <Button onClick={onSubmit} variant="contained" color="error">
              {marker ? 'Update' : 'Create'}
            </Button>
          </Stack>
        </FormProvider>
      </>
    </AppModal>
  );
};

export default CreateNewMarkerModal;
